// Root component — fetches auth state on mount, routes between Login and Gateway.
// Phase 0: simple branch by auth status. Phase 1+ introduces TanStack Router with full route tree.

import type React from 'react';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { LoginPage } from '@/pages/Login';
import { SignupPage } from '@/pages/Signup';
// Marketing pages (Phase 8.43): public, no auth required
const PricingPage = lazy(() => import('@/pages/marketing/Pricing').then((m) => ({ default: m.PricingPage })));
const StatusPage = lazy(() => import('@/pages/marketing/Status').then((m) => ({ default: m.StatusPage })));
const DocsPage = lazy(() => import('@/pages/marketing/Docs').then((m) => ({ default: m.DocsPage })));
const CaPortalPage = lazy(() => import('@/pages/Admin/CaPortal').then((m) => ({ default: m.CaPortalPage })));
const SubscriptionPage = lazy(() => import('@/pages/Admin/Subscription').then((m) => ({ default: m.SubscriptionPage })));
const PostingPeriodsPage = lazy(() => import('@/pages/Admin/PostingPeriods').then((m) => ({ default: m.PostingPeriodsPage })));
const RecurringVouchersPage = lazy(() => import('@/pages/Vouchers/Recurring').then((m) => ({ default: m.RecurringVouchersPage })));
const NewRecurringPage = lazy(() => import('@/pages/Vouchers/NewRecurring').then((m) => ({ default: m.NewRecurringPage })));
const BankReconcilePage = lazy(() => import('@/pages/Banking/Reconcile').then((m) => ({ default: m.BankReconcilePage })));
const Soc2Page = lazy(() => import('@/pages/Admin/Soc2').then((m) => ({ default: m.Soc2Page })));
const GroupCompaniesPage = lazy(() => import('@/pages/Admin/GroupCompanies').then((m) => ({ default: m.GroupCompaniesPage })));
const SecurityPolicyPage = lazy(() => import('@/pages/Admin/SecurityPolicy').then((m) => ({ default: m.SecurityPolicyPage })));
const StatutoryUpdatesPage = lazy(() => import('@/pages/Admin/StatutoryUpdates').then((m) => ({ default: m.StatutoryUpdatesPage })));
const SuspiciousActivityPage = lazy(() => import('@/pages/Admin/SuspiciousActivity').then((m) => ({ default: m.SuspiciousActivityPage })));
const PerformanceDashboardPage = lazy(() => import('@/pages/Admin/PerformanceDashboard').then((m) => ({ default: m.PerformanceDashboardPage })));
const ConsolidatedTbPage = lazy(() => import('@/pages/Admin/ConsolidatedTb').then((m) => ({ default: m.ConsolidatedTbPage })));
const ConsolidatedPnLPage = lazy(() => import('@/pages/Admin/ConsolidatedPnL').then((m) => ({ default: m.ConsolidatedPnLPage })));
const ConsolidatedGstr3bPage = lazy(() => import('@/pages/Admin/ConsolidatedGstr3b').then((m) => ({ default: m.ConsolidatedGstr3bPage })));
const ConsolidatedBsPage = lazy(() => import('@/pages/Admin/ConsolidatedBs').then((m) => ({ default: m.ConsolidatedBsPage })));
const VaultPage = lazy(() => import('@/pages/Admin/Vault').then((m) => ({ default: m.VaultPage })));
const Soc2EvidencePage = lazy(() => import('@/pages/Admin/Soc2Evidence').then((m) => ({ default: m.Soc2EvidencePage })));
const YearEndClosePage = lazy(() => import('@/pages/Admin/YearEndClose').then((m) => ({ default: m.YearEndClosePage })));
const JobsHealthPage = lazy(() => import('@/pages/Admin/JobsHealth').then((m) => ({ default: m.JobsHealthPage })));
const RetentionPage = lazy(() => import('@/pages/Admin/Retention').then((m) => ({ default: m.RetentionPage })));
const ApprovalsPage = lazy(() => import('@/pages/Admin/Approvals').then((m) => ({ default: m.ApprovalsPage })));
const RateLimitsPage = lazy(() => import('@/pages/Admin/RateLimits').then((m) => ({ default: m.RateLimitsPage })));
const EInvoiceQueuePage = lazy(() => import('@/pages/Admin/EInvoiceQueue').then((m) => ({ default: m.EInvoiceQueuePage })));
const TdsLiabilityPage = lazy(() => import('@/pages/Reports/TdsLiability').then((m) => ({ default: m.TdsLiabilityPage })));
const ReportShellPage = lazy(() => import('@/pages/Reports/Shell').then((m) => ({ default: m.ReportShellPage })));
import { GatewayPage } from '@/pages/Gateway';
import { NotFoundPage } from '@/pages/NotFound';

// Hot path — eager import for instant feel
import { VouchersListPage } from '@/pages/Vouchers/List';
import { VouchersSectionPage } from '@/pages/sections/VouchersSection';
import { MastersSectionPage } from '@/pages/sections/MastersSection';
import { BankingSectionPage } from '@/pages/sections/BankingSection';
import { VoucherDetailPage } from '@/pages/Vouchers/Detail';
import { DayBookPage } from '@/pages/Reports/DayBook';
import { ReportsHubPage } from '@/pages/Reports/Hub';
import { ReportsCataloguePage } from '@/pages/Reports/Catalogue';
import { BIHubPage } from '@/pages/BI/Hub';
import { CeoCockpitPage } from '@/pages/BI/CeoCockpit';

// Lazy chunks — code-split per page
const PartiesPage = lazy(() => import('@/pages/Masters/Parties').then((m) => ({ default: m.PartiesPage })));
const ItemsPage = lazy(() => import('@/pages/Masters/Items').then((m) => ({ default: m.ItemsPage })));
const LedgersPage = lazy(() => import('@/pages/Masters/Ledgers').then((m) => ({ default: m.LedgersPage })));
const VoucherTypesPage = lazy(() => import('@/pages/Masters/VoucherTypes').then((m) => ({ default: m.VoucherTypesPage })));
const HsnSacPage = lazy(() => import('@/pages/Masters/HsnSac').then((m) => ({ default: m.HsnSacPage })));
const UnitsPage = lazy(() => import('@/pages/Masters/Units').then((m) => ({ default: m.UnitsPage })));
const StockGroupsPage = lazy(() => import('@/pages/Masters/StockGroups').then((m) => ({ default: m.StockGroupsPage })));
const PriceLevelsPage = lazy(() => import('@/pages/Masters/PriceLevels').then((m) => ({ default: m.PriceLevelsPage })));
const CostCategoriesPage = lazy(() => import('@/pages/Masters/CostCategories').then((m) => ({ default: m.CostCategoriesPage })));
const CostCentresPage = lazy(() => import('@/pages/Masters/CostCentres').then((m) => ({ default: m.CostCentresPage })));
const TaxRatesPage = lazy(() => import('@/pages/Masters/TaxRates').then((m) => ({ default: m.TaxRatesPage })));
const NewSalesPage = lazy(() => import('@/pages/Vouchers/NewSales').then((m) => ({ default: m.NewSalesPage })));
const NewReceiptPage = lazy(() => import('@/pages/Vouchers/NewReceipt').then((m) => ({ default: m.NewReceiptPage })));
const NewPaymentPage = lazy(() => import('@/pages/Vouchers/NewPayment').then((m) => ({ default: m.NewPaymentPage })));
const NewContraPage = lazy(() => import('@/pages/Vouchers/NewContra').then((m) => ({ default: m.NewContraPage })));
const NewJournalPage = lazy(() => import('@/pages/Vouchers/NewJournal').then((m) => ({ default: m.NewJournalPage })));
const NewPurchasePage = lazy(() => import('@/pages/Vouchers/NewPurchase').then((m) => ({ default: m.NewPurchasePage })));
const NewCreditNotePage = lazy(() => import('@/pages/Vouchers/NewCreditNote').then((m) => ({ default: m.NewCreditNotePage })));
const NewDebitNotePage = lazy(() => import('@/pages/Vouchers/NewDebitNote').then((m) => ({ default: m.NewDebitNotePage })));
const TrialBalancePage = lazy(() => import('@/pages/Reports/TrialBalance').then((m) => ({ default: m.TrialBalancePage })));
const PnLPage = lazy(() => import('@/pages/Reports/PnL').then((m) => ({ default: m.PnLPage })));
const BalanceSheetPage = lazy(() => import('@/pages/Reports/BalanceSheet').then((m) => ({ default: m.BalanceSheetPage })));
const ComparisonPage = lazy(() => import('@/pages/Reports/Comparison').then((m) => ({ default: m.ComparisonPage })));
const OutstandingPage = lazy(() => import('@/pages/Reports/Outstanding').then((m) => ({ default: m.OutstandingPage })));
const LedgerDetailPage = lazy(() => import('@/pages/Reports/LedgerDetail').then((m) => ({ default: m.LedgerDetailPage })));
const StockSummaryPage = lazy(() => import('@/pages/Reports/StockSummary').then((m) => ({ default: m.StockSummaryPage })));
const Gstr1Page = lazy(() => import('@/pages/Reports/Gstr1').then((m) => ({ default: m.Gstr1Page })));
const MsmePayablesPage = lazy(() => import('@/pages/Reports/MsmePayables').then((m) => ({ default: m.MsmePayablesPage })));
const CashFlowPage = lazy(() => import('@/pages/Reports/CashFlow').then((m) => ({ default: m.CashFlowPage })));
const ReorderPage = lazy(() => import('@/pages/Reports/Reorder').then((m) => ({ default: m.ReorderPage })));
const PartyStatementPage = lazy(() => import('@/pages/Reports/PartyStatement').then((m) => ({ default: m.PartyStatementPage })));
const RegisterPage = lazy(() => import('@/pages/Reports/Register').then((m) => ({ default: m.RegisterPage })));
const GroupSummaryPage = lazy(() => import('@/pages/Reports/GroupSummary').then((m) => ({ default: m.GroupSummaryPage })));
const Gstr3bPage = lazy(() => import('@/pages/Reports/Gstr3b').then((m) => ({ default: m.Gstr3bPage })));
const TallyOutboxPage = lazy(() => import('@/pages/Admin/TallyOutbox').then((m) => ({ default: m.TallyOutboxPage })));
const TenantPortalPage = lazy(() => import('@/pages/Admin/TenantPortal').then((m) => ({ default: m.TenantPortalPage })));
const UsersPage = lazy(() => import('@/pages/Admin/Users').then((m) => ({ default: m.UsersPage })));
const AuditLogPage = lazy(() => import('@/pages/Admin/AuditLog').then((m) => ({ default: m.AuditLogPage })));
const BranchesPage = lazy(() => import('@/pages/Admin/Branches').then((m) => ({ default: m.BranchesPage })));
const BankingImportPage = lazy(() => import('@/pages/Banking/Import').then((m) => ({ default: m.BankingImportPage })));
const HelpPage = lazy(() => import('@/pages/Help').then((m) => ({ default: m.HelpPage })));
const ProfilePage = lazy(() => import('@/pages/Profile').then((m) => ({ default: m.ProfilePage })));
// Manufacturing pages parked in v0.7.13 alongside the v0.6.6 backend parking — re-enable when schema rollout is sequenced.
const ItemDetailPage = lazy(() => import('@/pages/Reports/ItemDetail').then((m) => ({ default: m.ItemDetailPage })));
import { CommandPalette } from '@/components/CommandPalette';
import { KeymapHintFooter } from '@/components/KeymapHintFooter';
import { ToastHost, useToast } from '@/components/primitives/Toast';
import { useGlobalShortcuts } from '@/hooks/useGlobalShortcuts';
import { useTallyShortcut } from '@/hooks/useTallyShortcut';
import { isVoucherDirty } from '@/lib/voucherDirty';
import { collectCarry, setVoucherCarry } from '@/lib/voucherCarry';
import { navigateTo } from '@/lib/navigate';

// B-004: moved to lib/navigate.ts so all five call sites share one definition —
// re-exported here because this module's helpers below build on it.


// Shortcut navigation that won't silently blow away a half-entered voucher.
// An empty entry form navigates without asking (isVoucherDirty flips as the
// user types — see lib/voucherDirty.ts).
function guardedNavigate(path: string) {
  const here = window.location.pathname;
  if (here === path) return;
  if (here.startsWith('/vouchers/new') && isVoucherDirty() &&
      !window.confirm('Leave this voucher? Anything entered here will be discarded.')) return;
  navigateTo(path);
}

// Voucher-type switching (F4–F9, Alt+F5/F6) — Tally parity: switching type
// mid-entry does NOT discard. The active form's draft is collected and the
// target form re-applies whatever carries (party, date, narration; item lines
// between invoice types; journal lines between double-entry types). Leaving
// voucher entry for anything ELSE still confirms via guardedNavigate.
function switchVoucherType(path: string) {
  const here = window.location.pathname;
  if (here === path) return;
  if (here.startsWith('/vouchers/new') && isVoucherDirty()) {
    const draft = collectCarry();
    if (draft) {
      setVoucherCarry(draft);
      navigateTo(path);
      return;
    }
  }
  guardedNavigate(path);
}

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-snow">
      <div className="flex flex-col items-center gap-3">
        <img src="/brand/mark.svg" alt="" aria-hidden className="h-8 w-8 animate-pulse" />
        <span className="text-caption text-cool">Loading…</span>
      </div>
    </div>
  );
}

export function App() {
  const status = useAuthStore((s) => s.status);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const toast = useToast();

  // Global key listener — runs always, even on the Login page, so Ctrl+K works.
  useGlobalShortcuts(true);

  // Global navigation bindings — keep them here so they're live on every screen.
  // Every binding advertised in the keymap / Help page is wired here (or screen-level).
  useTallyShortcut('nav.gateway', () => guardedNavigate('/'));
  useTallyShortcut('window.help', () => guardedNavigate('/help'));
  useTallyShortcut('nav.admin', () => guardedNavigate('/admin'));

  // Vouchers — Tally F-keys (Ctrl+Shift+letter in modern mode). Same handler ids in both keymaps.
  useTallyShortcut('voucher.new.contra',      () => switchVoucherType('/vouchers/new/contra'));
  useTallyShortcut('voucher.new.payment',     () => switchVoucherType('/vouchers/new/payment'));
  useTallyShortcut('voucher.new.receipt',     () => switchVoucherType('/vouchers/new/receipt'));
  useTallyShortcut('voucher.new.journal',     () => switchVoucherType('/vouchers/new/journal'));
  useTallyShortcut('voucher.new.sales',       () => switchVoucherType('/vouchers/new/sales'));
  useTallyShortcut('voucher.new.purchase',    () => switchVoucherType('/vouchers/new/purchase'));
  useTallyShortcut('voucher.new.debit-note',  () => switchVoucherType('/vouchers/new/debit-note'));
  useTallyShortcut('voucher.new.credit-note', () => switchVoucherType('/vouchers/new/credit-note'));
  useTallyShortcut('voucher.list',            () => guardedNavigate('/vouchers/list'));

  // Reports
  useTallyShortcut('report.day-book',          () => guardedNavigate('/reports/day-book'));
  useTallyShortcut('report.trial-balance',     () => guardedNavigate('/reports/trial-balance'));
  useTallyShortcut('report.pnl',               () => guardedNavigate('/reports/pnl'));
  useTallyShortcut('report.balance-sheet',     () => guardedNavigate('/reports/balance-sheet'));
  useTallyShortcut('report.cash-flow',         () => guardedNavigate('/reports/cash-flow'));
  useTallyShortcut('report.outstanding',       () => guardedNavigate('/reports/outstanding'));
  useTallyShortcut('report.party-statement',   () => guardedNavigate('/reports/party-statement'));
  useTallyShortcut('report.group-summary',     () => guardedNavigate('/reports/group-summary'));
  useTallyShortcut('report.stock-summary',     () => guardedNavigate('/reports/stock-summary'));
  useTallyShortcut('report.reorder',           () => guardedNavigate('/reports/reorder'));
  useTallyShortcut('report.msme',              () => guardedNavigate('/reports/msme-payables'));
  useTallyShortcut('report.tds',               () => guardedNavigate('/reports/tds-liability'));
  useTallyShortcut('report.gstr-1',            () => guardedNavigate('/reports/gstr-1'));
  useTallyShortcut('report.gstr-3b',           () => guardedNavigate('/reports/gstr-3b'));
  useTallyShortcut('report.sales-register',    () => guardedNavigate('/reports/sales-register'));
  useTallyShortcut('report.purchase-register', () => guardedNavigate('/reports/purchase-register'));

  // Masters
  useTallyShortcut('master.parties',       () => guardedNavigate('/masters/parties'));
  useTallyShortcut('master.items',         () => guardedNavigate('/masters/items'));
  useTallyShortcut('master.ledgers',       () => guardedNavigate('/masters/ledgers'));
  useTallyShortcut('master.voucher-types', () => guardedNavigate('/masters/voucher-types'));
  useTallyShortcut('master.hsn',           () => guardedNavigate('/masters/hsn-sac'));

  // Export / Print — fire the visible button when the screen exposes one.
  useTallyShortcut('edit.export', () => {
    (document.querySelector('[data-shortcut="export"]') as HTMLElement | null)?.click();
  });
  useTallyShortcut('edit.print', () => {
    const btn = document.querySelector('[data-shortcut="print"]') as HTMLElement | null;
    if (btn) { btn.click(); return; }
    // Only print screens designed for paper. Blind window.print() on the
    // Gateway froze the browser for ages — its blur/gradient layers take
    // forever to rasterize and the page has no print layout anyway.
    const p = window.location.pathname;
    const printable = p.startsWith('/reports') || p.startsWith('/r/') || /^\/vouchers\/[^/]+$/.test(p) || p.startsWith('/admin/group-companies/');
    if (printable) window.print();
    else toast.info('Nothing to print here', 'Open a report or a voucher, then press Ctrl+P.');
  });

  // ── Global Esc ladder (Tally drill-out) ─────────────────────
  // Esc backs out one level from ANY screen: field → blur; open dialog/menu →
  // its own handler closes it; report/master/admin page → its hub; hub → the
  // Gateway. Voucher entry forms own their Esc (confirm-discard → list).
  useEffect(() => {
    function parentOf(path: string): string | null {
      if (path === '/' || path === '/login' || path === '/signup') return null;
      if (path.startsWith('/vouchers/new')) return null;        // entry forms own Esc
      if (path.startsWith('/r/')) return '/reports-catalogue';  // engine report → catalogue
      if (path === '/reports-catalogue') return '/reports';
      if (path.startsWith('/admin/group-companies/')) return '/admin/group-companies';
      if (path.startsWith('/admin/soc2/')) return '/admin/soc2';
      const hubs = ['/reports', '/masters', '/admin', '/banking', '/bi', '/vouchers'];
      if (hubs.includes(path)) return '/';
      for (const h of hubs) if (path.startsWith(h + '/')) return h;
      return '/'; // /help /profile /pricing /status /docs …
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // A dialog/drawer/menu is open — its own Escape handler closes it.
      if (document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]')) return;
      // First Esc backs out of the focused field (Tally behaviour).
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) {
        el.blur();
        return;
      }
      const parent = parentOf(window.location.pathname);
      if (parent !== null) {
        e.preventDefault();
        guardedNavigate(parent);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Warm every route chunk during idle time after login — 70 pages are
  // lazy-loaded, so first navigation to each paid a chunk round-trip ("pages
  // take time to load"). Prefetching makes every later click render from
  // cache in a blink; 3 chunks at a time so real traffic always wins.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const thunks = Object.values(import.meta.glob('./pages/**/*.tsx'));
    let i = 0;
    let cancelled = false;
    const idle = (cb: () => void) =>
      'requestIdleCallback' in window ? (window as Window & typeof globalThis).requestIdleCallback(cb, { timeout: 2000 }) : setTimeout(cb, 300);
    const step = () => {
      if (cancelled) return;
      for (let n = 0; n < 3 && i < thunks.length; n++, i++) void (thunks[i]() as Promise<unknown>).catch(() => {});
      if (i < thunks.length) idle(step);
    };
    const t = setTimeout(() => idle(step), 1500); // let first paint + data land first
    return () => { cancelled = true; clearTimeout(t); };
  }, [status]);

  // Re-render on popstate so command-palette navigation works without a router.
  // Phase 1 swaps this for TanStack Router and removes the manual state.
  const [, setPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  useEffect(() => {
    function onPop() { setPath(window.location.pathname); }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Once authenticated, /login and /signup are meaningless — canonicalise to
  // the Gateway. Without this, logging in while ON /login left the user on a
  // 404 (the authenticated route table has no /login entry).
  useEffect(() => {
    if (status !== 'authenticated') return;
    const p = window.location.pathname;
    if (p === '/login' || p === '/signup') {
      window.history.replaceState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [status]);

  // Boot probe — call /me to discover whether we have a valid session.
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const res = await api.get('/auth/me');
      return res.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: status === 'unknown' || status === 'authenticated',
  });

  useEffect(() => {
    if (meQuery.isSuccess) {
      setAuthenticated({
        user: meQuery.data.user,
        tenant: meQuery.data.tenant,
        companies: meQuery.data.companies,
        activeCompanyId: meQuery.data.activeCompanyId,
        activeBranchId: meQuery.data.activeBranchId,
        supplierStateCode: meQuery.data.supplierStateCode ?? null,
      });
    } else if (meQuery.isError) {
      const err = meQuery.error;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setUnauthenticated();
      } else {
        setUnauthenticated('Cannot reach the server. Retry?');
      }
    }
  }, [meQuery.isSuccess, meQuery.isError, meQuery.data, meQuery.error, setAuthenticated, setUnauthenticated]);

  if (status === 'unknown' || (status === 'authenticated' && meQuery.isFetching && !meQuery.data)) {
    return <Loading />;
  }

  if (status === 'unauthenticated') {
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    if (path === '/signup') return <SignupPage />;
    if (path === '/help')   return <Suspense fallback={<Loading />}><HelpPage /></Suspense>;
    if (path === '/pricing') return <Suspense fallback={<Loading />}><PricingPage /></Suspense>;
    if (path === '/status')  return <Suspense fallback={<Loading />}><StatusPage /></Suspense>;
    if (path === '/docs')    return <Suspense fallback={<Loading />}><DocsPage /></Suspense>;
    // Any app path while logged out → the login screen, WITHOUT navigating away:
    // after sign-in the router renders the URL they were trying to reach.
    // (This used to show the 404 page — an expired session made every deep link
    // claim "that URL isn't anywhere on Orenza Billing".)
    return <LoginPage />;
  }

  if (status === 'authenticated') {
    // Simple path-based router. Phase 2+ swaps for TanStack Router with a real route tree.
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    let Page: React.ComponentType = NotFoundPage;
    if (path === '/' || path === '/gateway' || path === '/login' || path === '/signup') Page = GatewayPage;
    else if (path === '/masters/parties') Page = PartiesPage;
    else if (path === '/masters/items') Page = ItemsPage;
    else if (path === '/masters/ledgers') Page = LedgersPage;
    else if (path === '/masters/voucher-types') Page = VoucherTypesPage;
    else if (path === '/masters/hsn-sac') Page = HsnSacPage;
    else if (path === '/masters/units') Page = UnitsPage;
    else if (path === '/masters/stock-groups') Page = StockGroupsPage;
    else if (path === '/masters/price-levels') Page = PriceLevelsPage;
    else if (path === '/masters/cost-categories') Page = CostCategoriesPage;
    else if (path === '/masters/cost-centres') Page = CostCentresPage;
    else if (path === '/masters/tax-rates') Page = TaxRatesPage;
    else if (path === '/vouchers') Page = VouchersSectionPage;
    else if (path === '/vouchers/list') Page = VouchersListPage;
    else if (path === '/vouchers/new/sales') Page = NewSalesPage;
    else if (path === '/vouchers/new/receipt') Page = NewReceiptPage;
    else if (path === '/vouchers/new/payment') Page = NewPaymentPage;
    else if (path === '/vouchers/new/contra') Page = NewContraPage;
    else if (path === '/vouchers/new/journal') Page = NewJournalPage;
    else if (path === '/vouchers/new/purchase') Page = NewPurchasePage;
    else if (path === '/vouchers/new/credit-note') Page = NewCreditNotePage;
    else if (path === '/vouchers/new/debit-note') Page = NewDebitNotePage;
    else if (path === '/vouchers/recurring') Page = RecurringVouchersPage;
    else if (path === '/vouchers/recurring/new') Page = NewRecurringPage;
    else if (/^\/vouchers\/[^/]+$/.test(path)) Page = VoucherDetailPage;
    else if (path === '/reports' || path === '/reports/') Page = ReportsHubPage;
    else if (path === '/reports-catalogue' || path === '/reports/catalogue') Page = ReportsCataloguePage;
    else if (path === '/bi' || path === '/bi/') Page = BIHubPage;
    else if (path === '/bi/ceo-cockpit') Page = CeoCockpitPage;
    else if (path === '/reports/day-book') Page = DayBookPage;
    else if (path === '/reports/trial-balance') Page = TrialBalancePage;
    else if (path === '/reports/pnl' || path === '/reports/profit-loss') Page = PnLPage;
    else if (path === '/reports/balance-sheet') Page = BalanceSheetPage;
    else if (path === '/reports/comparison') Page = ComparisonPage;
    else if (path === '/reports/outstanding') Page = OutstandingPage;
    else if (/^\/reports\/ledger\/[^/]+$/.test(path)) Page = LedgerDetailPage;
    else if (path === '/reports/stock-summary') Page = StockSummaryPage;
    else if (path === '/reports/gstr-1' || path === '/gst/gstr-1') Page = Gstr1Page;
    else if (path === '/reports/msme-payables') Page = MsmePayablesPage;
    else if (path === '/reports/cash-flow') Page = CashFlowPage;
    else if (path === '/reports/reorder') Page = ReorderPage;
    else if (path === '/reports/party-statement') Page = PartyStatementPage;
    else if (path === '/reports/sales-register' || path === '/reports/purchase-register' || path === '/reports/credit-note-register' || path === '/reports/debit-note-register') Page = RegisterPage;
    else if (path === '/reports/group-summary') Page = GroupSummaryPage;
    else if (path === '/reports/gstr-3b' || path === '/gst/gstr-3b') Page = Gstr3bPage;
    else if (/^\/reports\/item\/[^/]+$/.test(path)) Page = ItemDetailPage;
    else if (path === '/admin/tally-outbox') Page = TallyOutboxPage;
    else if (path === '/admin' || path === '/admin/' || path === '/admin/companies') Page = TenantPortalPage;
    else if (path === '/admin/users') Page = UsersPage;
    else if (path === '/admin/audit-log') Page = AuditLogPage;
    else if (path === '/admin/branches') Page = BranchesPage;
    else if (path === '/banking' || path === '/banking/') Page = BankingSectionPage;
    else if (path === '/banking/import') Page = BankingImportPage;
    else if (path === '/masters' || path === '/masters/') Page = MastersSectionPage;
    else if (path === '/help') Page = HelpPage;
    else if (path === '/profile' || path === '/me') Page = ProfilePage;
    else if (path === '/pricing') Page = PricingPage;
    else if (path === '/status') Page = StatusPage;
    else if (path === '/docs') Page = DocsPage;
    else if (path === '/admin/ca-portal') Page = CaPortalPage;
    else if (path === '/admin/subscription' || path === '/admin/billing') Page = SubscriptionPage;
    else if (path === '/admin/posting-periods' || path === '/admin/close') Page = PostingPeriodsPage;
    else if (path === '/banking/reconcile' || path === '/banking/rec') Page = BankReconcilePage;
    else if (path === '/admin/soc2' || path === '/admin/soc-2') Page = Soc2Page;
    else if (path === '/admin/group-companies') Page = GroupCompaniesPage;
    else if (/^\/admin\/group-companies\/[^/]+\/consolidated-tb$/.test(path)) Page = ConsolidatedTbPage;
    else if (/^\/admin\/group-companies\/[^/]+\/consolidated-pnl$/.test(path)) Page = ConsolidatedPnLPage;
    else if (/^\/admin\/group-companies\/[^/]+\/consolidated-gstr-3b$/.test(path)) Page = ConsolidatedGstr3bPage;
    else if (/^\/admin\/group-companies\/[^/]+\/consolidated-bs$/.test(path)) Page = ConsolidatedBsPage;
    else if (path === '/admin/vault' || path === '/admin/tally-vault') Page = VaultPage;
    else if (path === '/admin/soc2/evidence' || path === '/admin/evidence') Page = Soc2EvidencePage;
    else if (path === '/admin/year-end' || path === '/admin/year-end-close') Page = YearEndClosePage;
    else if (path === '/admin/jobs' || path === '/admin/jobs-health') Page = JobsHealthPage;
    else if (path === '/admin/retention') Page = RetentionPage;
    else if (path === '/admin/approvals') Page = ApprovalsPage;
    else if (path === '/admin/rate-limits') Page = RateLimitsPage;
    else if (path === '/admin/security-policy') Page = SecurityPolicyPage;
    else if (path === '/admin/statutory' || path === '/admin/statutory-updates') Page = StatutoryUpdatesPage;
    else if (path === '/admin/suspicious-activity' || path === '/admin/security/alerts') Page = SuspiciousActivityPage;
    else if (path === '/admin/performance' || path === '/admin/perf') Page = PerformanceDashboardPage;
    else if (path === '/admin/e-invoice-queue' || path === '/admin/e-invoice') Page = EInvoiceQueuePage;
    else if (path === '/reports/tds-liability' || path === '/reports/tds') Page = TdsLiabilityPage;
    else if (/^\/r\/[\w.-]+$/.test(path)) Page = ReportShellPage;
    // /manufacturing/* parked — falls through to NotFoundPage with suggestions
    return (
      <>
        <Suspense fallback={<Loading />}>
          {/* pb-12 keeps content above the fixed KeymapHintFooter strip */}
          <div className="pb-12">
            <Page />
          </div>
        </Suspense>
        <CommandPalette />
        <KeymapHintFooter />
        <ToastHost />
      </>
    );
  }

  return <Loading />;
}
